import { AxiosInstance } from 'axios';
import axiosClient from './axiosClientConfig';
import {
	ActionRequired,
	CaseApplications,
	CasesClient,
	CaseStatusFilter,
	FileType,
	HttpResponse,
} from '@/interfaces';
import {
	AccountDetailsResponse,
	ClientProfileDashboard,
	UpdateClientProfile,
} from '@/interfaces/clientProfile';
import { ListBrandsByClient } from '@/interfaces/brands';
import { AiDocumentEvaluation } from '@/interfaces/clientDocumentation';

class ClientProfile {
	constructor(private client: AxiosInstance) {}

	async getAccountDetails(
		clientId: string
	): Promise<HttpResponse<AccountDetailsResponse>> {
		return this.client(`client-profile/account-details/${clientId}`);
	}

	async updateClientProfileById(
		clientId: string,
		clientData: Partial<UpdateClientProfile>
	): Promise<HttpResponse<any>> {
		return this.client.patch(
			`/client-profile/account-details/${clientId}`,
			clientData
		);
	}

	async getDashboard(
		id: string
	): Promise<HttpResponse<ClientProfileDashboard>> {
		return this.client.get(`/client-profile/dashboard/${id}`);
	}

	async getClientCases({
		page,
		limit,
		search,
		clientId,
		progressStatusFilter,
	}: {
		page: number;
		limit: number;
		clientId: string;
		search?: string;
		progressStatusFilter?: CaseStatusFilter;
	}): Promise<HttpResponse<CasesClient>> {
		const params = {
			page: String(page),
			limit: String(limit),
			...(search && { search }),
			...(progressStatusFilter && { progressStatusFilter }),
		};
		const searchParams = new URLSearchParams(params);
		return this.client.post(`/client-profile/client-cases/?${searchParams}`, {
			clientId,
		});
	}

	async getClientCaseById(
		caseId: string,
		clientId: string
	): Promise<HttpResponse<CaseApplications>> {
		return this.client.post(`/client-profile/client-cases/${caseId}`, {
			clientId,
		});
	}

	async getClientActionsRequiredPending(
		caseId: string,
		clientId: string
	): Promise<HttpResponse<ActionRequired[]>> {
		return this.client.post(
			`/client-profile/action-required/pending/${caseId}`,
			{ clientId }
		);
	}

	async getClientActionsRequiredFiles(
		applicationId: number,
		clientId: string,
		includeFollowUpsQuestion?: boolean
	): Promise<HttpResponse<FileType[]>> {
		return this.client.post(
			`/client-profile/action-required/files/${applicationId}`,
			{ clientId, includeFollowUps: includeFollowUpsQuestion }
		);
	}

	async removeClientActionRequiredFile(
		actionId: number
	): Promise<HttpResponse<any>> {
		return this.client.delete(`/client-profile/action-required/${actionId}`);
	}

	async getAiDocumentEvaluation(
		applicationId: number
	): Promise<HttpResponse<AiDocumentEvaluation>> {
		return this.client.get(`/ai-document-evaluation/${applicationId}`);
	}

	async getAiSummary(applicationId: number) {
		return this.client.get(
			`/ai-document-evaluation/ai-summary/${applicationId}`
		);
	}

	async getBrandsUsed({
		page,
		brandId,
		categoryId,
		search,
		clientId,
		dateFrom,
		dateTo,
	}: {
		page: string;
		clientId: string;
		brandId?: string;
		categoryId?: string;
		search?: string;
		dateFrom?: string;
		dateTo?: string;
	}): Promise<HttpResponse<ListBrandsByClient>> {
		const params = {
			page,
			limit: 10,
			...(brandId && { brandId }),
			...(categoryId && { caseCategoryId: categoryId }),
			...(search && { search }),
			...(dateFrom && { dateFrom }),
			...(dateTo && { dateTo }),
		};
		return this.client.get(`/client-profile/brands-used/${clientId}`, {
			params,
		});
	}
}

export const clientProfileService = new ClientProfile(axiosClient);
